/** Lab create/delete wait long enough for SSH + useradd + VNC (do not abort at 3 minutes). */

export const PMA_LAB_OP_TIMEOUT_PER_USER_MS = 10 * 60 * 1000;
export const PMA_LAB_OP_TIMEOUT_FLOOR_MS = 3 * 60 * 1000;

export const PMA_CREATE_PROGRESS_STAGES = Object.freeze([
  { at: 4, label: 'Validating UserGen rows…' },
  { at: 12, label: 'Resolving lab hosts…' },
  { at: 22, label: 'Connecting over SSH…' },
  { at: 34, label: 'Creating Linux accounts…' },
  { at: 48, label: 'Writing user config…' },
  { at: 60, label: 'Setting passwords…' },
  { at: 72, label: 'Starting VNC services…' },
  { at: 82, label: 'Verifying VNC is listening…' },
  { at: 90, label: 'Building credentials Excel…' },
  { at: 96, label: 'Preparing download…' },
]);

export const PMA_CREATE_PROGRESS_DONE = Object.freeze({
  at: 100,
  label: 'Credentials Excel ready',
});

export const PMA_DELETE_PROGRESS_STAGES = Object.freeze([
  { at: 4, label: 'Validating users to delete…' },
  { at: 15, label: 'Resolving lab hosts…' },
  { at: 28, label: 'Connecting over SSH…' },
  { at: 42, label: 'Stopping VNC services…' },
  { at: 55, label: 'Removing VNC entries…' },
  { at: 70, label: 'Deleting Linux accounts…' },
  { at: 82, label: 'Removing cfg / cron.deny…' },
  { at: 92, label: 'Verifying users are gone…' },
]);

export const PMA_DELETE_PROGRESS_DONE = Object.freeze({
  at: 100,
  label: 'Lab delete finished',
});

export function labOpRequestTimeoutMs(userCount = 1) {
  const n = Math.max(1, Number(userCount) || 1);
  return Math.max(PMA_LAB_OP_TIMEOUT_FLOOR_MS, n * PMA_LAB_OP_TIMEOUT_PER_USER_MS);
}

export function labOpUserLabel(student, index = 0) {
  if (!student || typeof student !== 'object') return `user ${index + 1}`;
  return (
    String(student.unixUsername || student.unix_username || '').trim() ||
    String(student.email || student.emailAddress || student.email_address || '').trim() ||
    `user ${index + 1}`
  );
}

export function labOpUserLabels(studentIds = [], selectedStudents = []) {
  const byId = new Map(
    (selectedStudents || []).filter((s) => s && s.id != null).map((s) => [s.id, s])
  );
  return (studentIds || []).map((id, index) => {
    const match = byId.get(id) || selectedStudents[index];
    return labOpUserLabel(match, index);
  });
}

function withinUserFraction(elapsedMs) {
  const t = Math.max(0, Number(elapsedMs) || 0) / 1000;
  return Math.min(0.92, 0.92 * (1 - Math.exp(-t / 20)));
}

export function createUserProgress({
  index = 0,
  total = 1,
  userName = '',
  host = '',
  elapsedMs = 0,
  completed = 0,
  verb = 'Creating',
} = {}) {
  const n = Math.max(1, Number(total) || 1);
  const i = Math.max(0, Number(index) || 0);
  const doneCount = Math.max(0, Number(completed) || 0);
  const perUser = 100 / n;
  const percent = Math.min(96, Math.round(doneCount * perUser + withinUserFraction(elapsedMs) * perUser));
  const name = String(userName || `user ${i + 1}`).trim();
  const where = String(host || '').trim();
  const label = where
    ? `${verb} ${name} on ${where} (${i + 1} of ${n})…`
    : `${verb} ${name} (${i + 1} of ${n})…`;
  return { percent, label, currentUser: name, index: i, total: n };
}

export function createProgressAtElapsed(elapsedMs, { active = true } = {}) {
  if (!active) return { ...PMA_CREATE_PROGRESS_DONE };
  const t = Math.max(0, Number(elapsedMs) || 0) / 1000;
  const raw = 96 * (1 - Math.exp(-t / 18));
  const percent = Math.min(96, Math.max(4, Math.round(raw)));
  let label = PMA_CREATE_PROGRESS_STAGES[0].label;
  for (const stage of PMA_CREATE_PROGRESS_STAGES) {
    if (percent >= stage.at) label = stage.label;
  }
  return { percent, label };
}

export function deleteProgressAtElapsed(elapsedMs, { active = true } = {}) {
  if (!active) return { ...PMA_DELETE_PROGRESS_DONE };
  const t = Math.max(0, Number(elapsedMs) || 0) / 1000;
  const raw = 96 * (1 - Math.exp(-t / 14));
  const percent = Math.min(96, Math.max(4, Math.round(raw)));
  let label = PMA_DELETE_PROGRESS_STAGES[0].label;
  for (const stage of PMA_DELETE_PROGRESS_STAGES) {
    if (percent >= stage.at) label = stage.label;
  }
  return { percent, label };
}

export function emptyLabOpAggregate() {
  return {
    successCount: 0,
    errorCount: 0,
    totalRows: 0,
    issues: [],
    users: [],
    reports: [],
    messages: [],
  };
}

export function mergeLabOpResponse(agg, payload) {
  const next = agg || emptyLabOpAggregate();
  const sms = payload?.sms || {};
  const report = payload?.report || sms.report || null;
  const issues = payload?.issues || sms.issues || [];
  const users = sms.users || [];
  return {
    successCount: next.successCount + Number(sms.successCount || 0),
    errorCount: next.errorCount + Number(sms.errorCount || 0),
    totalRows: next.totalRows + Number(sms.totalRows || users.length || (payload ? 1 : 0)),
    issues: [...next.issues, ...issues],
    users: [...next.users, ...users],
    reports: report ? [...next.reports, report] : next.reports,
    messages: payload?.message ? [...next.messages, payload.message] : next.messages,
  };
}

export function labOpAggregateResult(agg, { verb = 'Created' } = {}) {
  const successCount = agg?.successCount || 0;
  const errorCount = agg?.errorCount || 0;
  const report = (agg?.reports || []).length ? agg.reports[agg.reports.length - 1] : null;
  const message =
    errorCount === 0
      ? `${verb} ${successCount} Linux account(s)`
      : `${verb} ${successCount} Linux account(s); ${errorCount} failed`;
  return {
    message,
    sms: {
      message,
      successCount,
      errorCount,
      totalRows: agg?.totalRows || 0,
      issues: agg?.issues || [],
      users: agg?.users || [],
      report,
    },
    report,
    issues: agg?.issues || [],
  };
}
